import React, { useEffect, useState, useContext } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Modal,
  Form,
  Alert,
  Spinner,
  Badge,
  Accordion
} from 'react-bootstrap';
import { mgbSetupAPI, recordAPI } from '../api/api';
import { AuthContext } from '../context/AuthContext';
import './MGBSetup.css';

function MGBSetup() {
  const { user } = useContext(AuthContext);
  const [tables, setTables] = useState([]);
  const [availableTables, setAvailableTables] = useState([]);
  const [availableViews, setAvailableViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFieldsModal, setShowFieldsModal] = useState(false);
  
  // Form states
  const [selectedTable, setSelectedTable] = useState('');
  const [formData, setFormData] = useState({
    tableName: '',
    headerLabel: '',
    xmltag: '',
    viewtable: '',
    uniqueField: ''
  });
  const [editData, setEditData] = useState({
    tableID: null,
    headerLabel: '',
    xmltag: '',
    viewtable: '',
    where1: '',
    orderby1: '',
    table1: '',
    uniqueField: '',
    useDistinct: false,
    searchable: false,
    useAlphaHeader: false,
    longViewLinkfromTable: false,
    editViewLinkfromTable: false,
    viewChildLinkfromTable: false,
    addnewLinkfromTable: false,
    deletefromTableLink: false,
    showDescription: false,
    showNumberofRecords: false,
    alternateColors: false,
    allowRadioButton: false,
    allowCheckbox: false,
    useAC: false
  });
  
  const [fieldsData, setFieldsData] = useState([]);
  const [selectedTableForFields, setSelectedTableForFields] = useState(null);
  const [editingFields, setEditingFields] = useState({});
  const [selectedFields, setSelectedFields] = useState({});
  
  // Field type configuration state
  const [dropdownTables, setDropdownTables] = useState([]);
  const [dropdownTableFields, setDropdownTableFields] = useState({});
  const [allXbyXConfigs, setAllXbyXConfigs] = useState([]);
  
  // Format state
  const [formats, setFormats] = useState([]);
  const [addFormats, setAddFormats] = useState([]);
  const [editFormats, setEditFormats] = useState([]);
  const [searchFormats, setSearchFormats] = useState([]);
  const [searchResultFormats, setSearchResultFormats] = useState([]);

  // Load XbyX configurations
  const loadXbyXConfigs = async () => {
    try {
      const response = await recordAPI.getAllXbyXConfigs();
      if (response.data.success && response.data.data) {
        setAllXbyXConfigs(response.data.data);
        response.data.data.forEach(config => {
        });
      } else {
      }
    } catch (err) {
    }
  };

  // Load formats
  const loadFormats = async () => {
    try {
      const response = await mgbSetupAPI.getFormats();
      
      if (response && response.data && response.data.success && response.data.formats) {
        const formats = response.data.formats;
        setFormats(formats);
        
        const add = formats.filter(f => f.FormatType === 'Add');
        const edit = formats.filter(f => f.FormatType === 'Edit');
        const search = formats.filter(f => f.FormatType === 'Search');
        const searchResult = formats.filter(f => f.FormatType === 'SearchResult');
        
        setAddFormats(add);
        setEditFormats(edit);
        setSearchFormats(search);
        setSearchResultFormats(searchResult);
      } else {
        setError('Failed to load formats.');
      }
    } catch (err) {
      setError('Failed to load formats: ' + (err.response?.data?.message || err.message));
    }
  };

  // Fetch registered tables
  const fetchTables = async () => {
    try {
      setLoading(true);
      const response = await mgbSetupAPI.getTables();
      if (response.data.success) {
        setTables(response.data.tables);
      }
    } catch (err) {
      setError('Failed to load registered tables. Please check the console for details.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch available tables
  const fetchAvailableTables = async () => {
    try {
      const response = await mgbSetupAPI.getAvailableTables();
      if (response.data.success) {
        setAvailableTables(response.data.tables);
      }
    } catch (err) {
      setError('Failed to load available tables');
    }
  };

  // Fetch available views
  const fetchAvailableViews = async () => {
    try {
      const response = await mgbSetupAPI.getAvailableViews();
      if (response.data.success) {
        setAvailableViews(response.data.views);
      }
    } catch (err) {
      // Don't show error - views are optional
    }
  };

  // Fetch fields for a specific table
  const fetchTableFields = async (tableName) => {
    try {
      if (!tableName) {
        setFieldsData([]);
        return;
      }
      
      // Use table-fields-info endpoint which includes primary key information
      const response = await mgbSetupAPI.getTableFields(tableName);
      
      if (response.data && response.data.success) {
        const fields = response.data.fields || [];
        setFieldsData(fields);
        // By default, select all fields
        const defaultSelection = {};
        fields.forEach((field, index) => {
          defaultSelection[index] = true; // Select all by default
        });
        setSelectedFields(defaultSelection);
      } else {
        setFieldsData([]);
        setError('Failed to load table fields: ' + (response.data?.message || 'Unknown error'));
      }
    } catch (err) {
      setFieldsData([]);
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
      } else {
        setError('Failed to load table fields. Please try again.');
      }
    }
  };

  // Fetch table fields for dropdown configuration
  const fetchDropdownTableFields = async (tableName) => {
    try {
      const response = await mgbSetupAPI.getTableFieldsInfo(tableName);
      if (response.data.success) {
        setDropdownTableFields(prev => ({
          ...prev,
          [tableName]: response.data.fields
        }));
      }
    } catch (err) {
    }
  };

  // Fetch registered table fields
  const fetchRegisteredTableFields = async (tableID) => {
    try {
      const response = await mgbSetupAPI.getTableFieldsList(tableID);
      if (response.data.success) {
        setFieldsData(response.data.fields);
        // Initialize editing state for each field
        const initialEditData = {};
        response.data.fields.forEach(field => {
          initialEditData[field.FieldsID] = {
            fieldname: field.fieldname,
            description: field.description,
            ordering: field.ordering,
            addable: field.addable,
            editable: field.editable,
            shortViewable: field.shortViewable,
            searchable: field.searchable,
            requiredForSearch: field.RequiredForSearch || false,
            type: field.type || 'text',
            sqlSource: field.SQLSource || '',
            sqlDisplay: field.SQLDisplay || '',
            sqlWhere: field.SQLWhere || '',
            encrypted: field.Encrypted || false,
            defaultValue: field.DefaultValue || '',
            subRecordID: (field.subRecordID !== null && field.subRecordID !== undefined) ? field.subRecordID : null,
            fieldControllerConfig: {}, // fieldController not yet implemented in database
            displayLength: field.displayLength || field.DisplayLength || '',
            maxLength: field.maxLength || field.MaxLength || '',
            height: field.height || field.Height || '',
            width: field.width || field.Width || ''
          };
        });
        setEditingFields(initialEditData);
      } else {
        setError('Failed to load table fields: ' + (response.data?.message || 'Unknown error'));
      }
    } catch (err) {
      setError('Failed to load table fields. Please try again.');
      setFieldsData([]);
      setEditingFields({});
    }
  };

  // Handle saving field changes
  const handleSaveFieldChanges = async () => {
    try {
      setError('');
      
      // Separate new fields (FieldsID < 0) from existing fields
      const newFields = [];
      const existingFields = [];

      fieldsData.forEach(field => {
        const fieldEditData = editingFields[field.FieldsID] || {};
        let fieldControllerJson = null;
        
        // Build fieldController JSON if controller config exists and field type is set
        if (fieldEditData.type && fieldEditData.fieldControllerConfig && Object.keys(fieldEditData.fieldControllerConfig).length > 0) {
          fieldControllerJson = JSON.stringify({
            type: fieldEditData.type,
            config: fieldEditData.fieldControllerConfig
          });
        }

        const fieldData = {
          FieldsID: field.FieldsID,
          fieldname: fieldEditData.fieldname || field.fieldname,
          description: fieldEditData.description || field.description,
          ordering: parseInt(fieldEditData.ordering) || parseInt(field.ordering) || 0,
          addable: Boolean(fieldEditData.addable ?? field.addable),
          editable: field.isPrimaryKey ? false : Boolean(fieldEditData.editable ?? field.editable),
          shortViewable: Boolean(fieldEditData.shortViewable ?? field.shortViewable),
          searchable: Boolean(fieldEditData.searchable ?? field.searchable),
          requiredForSearch: Boolean(fieldEditData.requiredForSearch ?? field.RequiredForSearch ?? false),
          type: fieldEditData.type || field.type || 'text',
          sqlSource: fieldEditData.sqlSource || field.SQLSource || '',
          sqlDisplay: fieldEditData.sqlDisplay || field.SQLDisplay || '',
          sqlWhere: fieldEditData.sqlWhere || field.SQLWhere || '',
          encrypted: Boolean(fieldEditData.encrypted ?? field.Encrypted),
          defaultValue: fieldEditData.defaultValue !== undefined && fieldEditData.defaultValue !== null && fieldEditData.defaultValue.toString().trim() !== '' 
            ? fieldEditData.defaultValue.toString().trim() 
            : (field.DefaultValue && field.DefaultValue.toString().trim() !== '' ? field.DefaultValue.toString().trim() : ''),
          subRecordID: (fieldEditData.subRecordID !== undefined) 
            ? (fieldEditData.subRecordID !== null && fieldEditData.subRecordID !== '' 
                ? (typeof fieldEditData.subRecordID === 'number' ? fieldEditData.subRecordID : parseInt(fieldEditData.subRecordID)) 
                : null)
            : (field.subRecordID !== null && field.subRecordID !== undefined ? field.subRecordID : null),
          fieldController: fieldControllerJson,
          // Use fieldEditData value if it exists (even if empty string means user cleared it), otherwise use field value
          displayLength: fieldEditData.displayLength !== undefined 
            ? (fieldEditData.displayLength.toString().trim() !== '' ? fieldEditData.displayLength.toString().trim() : null)
            : (field.displayLength && field.displayLength.toString().trim() !== '' ? field.displayLength.toString().trim() : null),
          maxLength: fieldEditData.maxLength !== undefined 
            ? (fieldEditData.maxLength.toString().trim() !== '' ? fieldEditData.maxLength.toString().trim() : null)
            : (field.maxLength && field.maxLength.toString().trim() !== '' ? field.maxLength.toString().trim() : null),
          height: fieldEditData.height !== undefined 
            ? (fieldEditData.height.toString().trim() !== '' ? fieldEditData.height.toString().trim() : null)
            : (field.height && field.height.toString().trim() !== '' ? field.height.toString().trim() : null),
          width: fieldEditData.width !== undefined 
            ? (fieldEditData.width.toString().trim() !== '' ? fieldEditData.width.toString().trim() : null)
            : (field.width && field.width.toString().trim() !== '' ? field.width.toString().trim() : null),
          datatype: fieldEditData.datatype || field.datatype || field.dataType || null
        };

        // Check if it's a new field (FieldsID < 0)
        if (field.FieldsID < 0) {
          newFields.push(fieldData);
        } else {
          existingFields.push(fieldData);
        }
      });

      const allFields = existingFields.concat(newFields);

      // Call API with both new and existing fields
      const response = await mgbSetupAPI.updateFields(
        selectedTableForFields.tableID,
        allFields
      );

      if (response.data.success) {
        setSuccess('Field changes saved successfully');
        setShowFieldsModal(false);
        fetchRegisteredTableFields(selectedTableForFields.tableID);
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(response.data.message || 'Failed to save field changes');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Error saving field changes';
      const errorDetails = err.response?.data?.details || '';
      setError(errorMsg + (errorDetails ? ` - ${errorDetails}` : ''));
    }
  };

  const handleAddNewField = () => {
    // Create a temporary field with a unique negative ID (for new fields)
    const newFieldID = Math.min(...fieldsData.map(f => f.FieldsID || 0), 0) - 1;
    
    const newField = {
      FieldsID: newFieldID,
      tableID: selectedTableForFields.tableID,
      fieldname: `NewField${Math.abs(newFieldID)}`,
      description: '',
      ordering: fieldsData.length + 1,
      addable: true,
      editable: true,
      shortViewable: true,
      searchable: false,
      type: 'text',
      SQLSource: '',
      SQLDisplay: '',
      SQLWhere: '',
      Encrypted: false,
      DefaultValue: '',
      isPrimaryKey: false,
      isIdentity: false
    };

    // Add to fieldsData
    setFieldsData([...fieldsData, newField]);

    // Initialize editing fields for the new field
    setEditingFields({
      ...editingFields,
      [newFieldID]: {
        fieldname: newField.fieldname,
        description: newField.description,
        ordering: newField.ordering,
        addable: newField.addable,
        editable: newField.editable,
        shortViewable: newField.shortViewable,
        searchable: newField.searchable,
        type: newField.type,
        sqlSource: newField.SQLSource,
        sqlDisplay: newField.SQLDisplay,
        sqlWhere: newField.SQLWhere,
        encrypted: newField.Encrypted,
        defaultValue: newField.DefaultValue
      }
    });
  };

  useEffect(() => {
    const loadData = async () => {
      if (!user) {
        return;
      }

      try {
        // First verify session is still valid
        const { authAPI } = await import('../api/api');
        await authAPI.checkSession();
        
        // Session is valid, now fetch data
        await Promise.all([
          fetchTables(),
          fetchAvailableTables(),
          fetchAvailableViews(),
          loadXbyXConfigs(),
          loadFormats()
        ]);
      } catch (err) {
        // Error loading initial data
      }
    };

    loadData();
  }, [user]);

  // Monitor allXbyXConfigs changes
  useEffect(() => {
    if (allXbyXConfigs && allXbyXConfigs.length > 0) {
    }
  }, [allXbyXConfigs]);

  // Load XbyX configs on component mount
  useEffect(() => {
    loadXbyXConfigs();
  }, []);

  // Handle Add Table
  const handleAddTable = async () => {
    if (!selectedTable) {
      setError('Please select a table');
      return;
    }

    if (!formData.uniqueField) {
      setError('Please select a unique field (primary key)');
      return;
    }

    // Get selected fields
    const fieldsToRegister = fieldsData
      .filter((_, index) => selectedFields[index])
      .map(field => field.fieldName);

    if (fieldsToRegister.length === 0) {
      setError('Please select at least one field to register');
      return;
    }

    try {
      setError('');
      setSuccess('');
      const response = await mgbSetupAPI.registerTable({
        tableName: selectedTable,
        headerLabel: formData.headerLabel || selectedTable,
        xmltag: formData.xmltag || selectedTable.toLowerCase(),
        viewtable: formData.viewtable || null,
        uniqueField: formData.uniqueField || null,
        selectedFields: fieldsToRegister
      });

      if (response.data.success) {
        setSuccess(`Table "${selectedTable}" registered successfully with ${response.data.fieldsCount} fields`);
        setShowAddModal(false);
        setSelectedTable('');
        setFormData({ tableName: '', headerLabel: '', xmltag: '', viewtable: '', uniqueField: '' });
        setSelectedFields({});
        fetchTables();
        fetchAvailableTables();
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Error registering table';
      setError(errorMsg);
    }
  };

  // Handle Edit Table
  const handleEditTable = async () => {
    try {
      setError('');
      const response = await mgbSetupAPI.updateTable(editData.tableID, {
        headerLabel: editData.headerLabel,
        xmltag: editData.xmltag,
        viewtable: editData.viewtable || null,
        where1: editData.where1,
        orderby1: editData.orderby1,
        table1: editData.table1,
        uniqueField: editData.uniqueField,
        useDistinct: editData.useDistinct,
        searchable: editData.searchable,
        useAlphaHeader: editData.useAlphaHeader,
        longViewLinkfromTable: editData.longViewLinkfromTable,
        editViewLinkfromTable: editData.editViewLinkfromTable,
        viewChildLinkfromTable: editData.viewChildLinkfromTable,
        addnewLinkfromTable: editData.addnewLinkfromTable,
        deletefromTableLink: editData.deletefromTableLink,
        showDescription: editData.showDescription,
        showNumberofRecords: editData.showNumberofRecords,
        alternateColors: editData.alternateColors,
        allowRadioButton: editData.allowRadioButton,
        allowCheckbox: editData.allowCheckbox,
        useAC: editData.useAC,
        addFormatID: editData.addFormatID || null,
        editFormatID: editData.editFormatID || null,
        searchFormatID: editData.searchFormatID || null,
        searchResultFormatID: editData.searchResultFormatID || null
      });
      if (response.data.success) {
        setSuccess('Table metadata updated successfully');
        setShowEditModal(false);
        fetchTables();
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Error updating table';
      setError(errorMsg);
    }
  };

  // Handle Delete Table
  const handleDeleteTable = async (tableID) => {
    if (!window.confirm('Are you sure you want to delete this table and all its fields?')) {
      return;
    }

    try {
      setError('');
      const response = await mgbSetupAPI.deleteTable(tableID);

      if (response.data.success) {
        setSuccess('Table unregistered successfully');
        fetchTables();
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Error deleting table';
      setError(errorMsg);
    }
  };

  // Open edit modal
  const openEditModal = async (table) => {
    // Ensure availableViews is loaded for the dropdown to properly display the selected value
    await fetchAvailableViews();
    const editDataToSet = {
      tableID: table.tableID,
      headerLabel: table.HeaderLabel || '',
      xmltag: table.xmltag || '',
      viewtable: table.viewtable || '',
      where1: table.where1 || '',
      orderby1: table.orderby1 || '',
      table1: table.table1 || '',
      uniqueField: table.uniqueField || '',
      useDistinct: table.UseDistinct || false,
      searchable: table.Searchable || false,
      useAlphaHeader: table.UseAlphaHeader || false,
      longViewLinkfromTable: table.LongViewLinkfromTable || false,
      editViewLinkfromTable: table.EditViewLinkfromTable || false,
      viewChildLinkfromTable: table.ViewChildLinkfromTable || false,
      addnewLinkfromTable: table.AddnewLinkfromTable || false,
      deletefromTableLink: table.DeletefromTableLink || false,
      showDescription: table.ShowDescription || false,
      showNumberofRecords: table.ShowNumberofRecords || false,
      alternateColors: table.AlternateColors || false,
      allowRadioButton: table.allowRadioButton || false,
      allowCheckbox: table.allowCheckbox || false,
      useAC: table.UseAC || false,
      addFormatID: table.AddFormatID || null,
      editFormatID: table.EditFormatID || null,
      searchFormatID: table.SearchFormatID || null,
      searchResultFormatID: table.SearchResultFormatID || null
    };
    setEditData(editDataToSet);
    
    setShowEditModal(true);
  };

  // Open fields modal
  const openFieldsModal = async (table) => {
    setSelectedTableForFields(table);
    
    // When opening fields modal, set the form data with the existing viewtable value
    if (table.viewtable) {
      setFormData({ ...formData, viewtable: table.viewtable });
    }
    
    await fetchRegisteredTableFields(table.tableID);
    
    // Load available tables for dropdown configuration
    try {
      const response = await mgbSetupAPI.getAvailableTables();
      if (response.data.success) {
        setDropdownTables(response.data.tables);
        
        // After fetching tables, fetch fields for any table that's used as a source
        // We'll do this by iterating through the fields after they're loaded
        try {
          const fieldsResponse = await mgbSetupAPI.getTableFieldsList(table.tableID);
          if (fieldsResponse.data.success && fieldsResponse.data.fields) {
            for (const field of fieldsResponse.data.fields) {
              if (field.SQLSource) {
                const tableName = field.SQLSource.match(/FROM\s+(\w+)/i)?.[1];
                if (tableName) {
                  await fetchDropdownTableFields(tableName);
                }
              }
            }
          }
        } catch (err) {
        }
      }
    } catch (err) {
    }
    setShowFieldsModal(true);
  };

  // Handle table selection in Add modal
  const handleTableSelect = async (e) => {
    const tableName = e.target.value;
    setSelectedTable(tableName);
    setFormData({
      tableName,
      headerLabel: tableName,
      xmltag: tableName.toLowerCase(),
      viewtable: '',
      uniqueField: ''
    });
    
    // Clear previous fields data
    setFieldsData([]);
    
    // Fetch fields for the selected table
    if (tableName) {
      await fetchTableFields(tableName);
    }
  };

  // Handle view selection - separate from table selection to avoid clearing table selection
  const handleViewSelect = (e) => {
    const selectedView = e.target.value;
    setFormData({ ...formData, viewtable: selectedView });
    // If a view is selected, fetch its fields
    if (selectedView) {
      fetchTableFields(selectedView);
    } else {
      // If view is cleared, fetch table fields again
      if (selectedTable) {
        fetchTableFields(selectedTable);
      }
    }
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="mgb-setup-page py-4">
      <Row className="mb-3">
        <Col>
          <h1 className="mb-1" style={{ fontSize: '24px' }}>🔧 MGB Setup</h1>
          <p className="text-muted" style={{ fontSize: '13px', marginBottom: '0' }}>
            Register database tables and automatically populate MGB_Tables and MGB_Fields metadata.
          </p>
        </Col>
      </Row>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      <Row className="mb-4">
        <Col md={2}>
          <Card className="stat-card">
            <Card.Body>
              <div className="stat-number">{tables.length}</div>
              <div className="stat-label">Registered Tables</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="stat-card">
            <Card.Body>
              <div className="stat-number">{availableTables.length}</div>
              <div className="stat-label">Available Tables</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={8}>
          <Button
            variant="primary"
            size="lg"
            onClick={() => {
              setShowAddModal(true);
              setSelectedTable('');
              setFormData({ tableName: '', headerLabel: '', xmltag: '' });
              setFieldsData([]);
            }}
            className="w-100"
          >
            + Add New Table
          </Button>
        </Col>
      </Row>

      {/* Registered Tables Table */}
      <Row>
        <Col>
          <Card className="table-card">
            <Card.Header>
              <h5 className="mb-0">Registered Tables</h5>
            </Card.Header>
            <Card.Body>
              {tables.length === 0 ? (
                <p className="text-muted">No tables registered yet. Click "Add New Table" to register one.</p>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Table ID</th>
                      <th>Table Name</th>
                      <th>Header Label</th>
                      <th>XML Tag</th>
                      <th>Fields</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tables.map((table) => (
                      <tr key={table.tableID}>
                        <td>
                          <Badge bg="secondary">{table.tableID}</Badge>
                        </td>
                        <td>
                          <strong>{table.ResolvedName || table.TableName}</strong>
                        </td>
                        <td>{table.HeaderLabel || '-'}</td>
                        <td>{table.xmltag || '-'}</td>
                        <td>
                          <Button
                            variant="info"
                            size="sm"
                            onClick={() => openFieldsModal(table)}
                            style={{ padding: '0.125rem 0.2rem', minWidth: 'auto', fontSize: '14px' }}
                            title="View Fields"
                          >
                            👁️
                          </Button>
                        </td>
                        <td style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-start' }}>
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={() => openEditModal(table)}
                            style={{ padding: '0.125rem 0.2rem', minWidth: 'auto', fontSize: '14px' }}
                            title="Edit"
                          >
                            ✏️
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteTable(table.tableID)}
                            style={{ padding: '0.125rem 0.2rem', minWidth: 'auto', fontSize: '14px' }}
                            title="Delete"
                          >
                            🗑️
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add Table Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Register New Table</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Select Table *</Form.Label>
              <Form.Select
                value={selectedTable}
                onChange={handleTableSelect}
                disabled={availableTables.length === 0}
              >
                <option value="">-- Choose a table --</option>
                {availableTables
                  .filter(t => !tables.some(reg => reg.ResolvedName === t.tableName))
                  .map((table) => (
                    <option key={table.tableName} value={table.tableName}>
                      {table.tableName}
                    </option>
                  ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Only unregistered tables are shown
              </Form.Text>
            </Form.Group>

            {selectedTable && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Header Label</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.headerLabel}
                    onChange={(e) =>
                      setFormData({ ...formData, headerLabel: e.target.value })
                    }
                    placeholder="Display name for the table"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>XML Tag</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.xmltag}
                    onChange={(e) =>
                      setFormData({ ...formData, xmltag: e.target.value })
                    }
                    placeholder="XML tag name"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>View Table (Optional)</Form.Label>
                  <Form.Select
                    value={formData.viewtable || ''}
                    onChange={handleViewSelect}
                  >
                    <option value="">-- No View (Use Table Fields) --</option>
                    {availableViews && availableViews.length > 0 ? (
                      availableViews.map((view) => (
                        <option key={view.viewName} value={view.viewName}>
                          {view.viewName}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>Loading views...</option>
                    )}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {availableViews && availableViews.length > 0
                      ? `Select a view to use its fields instead of the table fields (${availableViews.length} views available)`
                      : 'Loading views...'}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Unique Field (Primary Key) *</Form.Label>
                  <Form.Select
                    value={formData.uniqueField}
                    onChange={(e) =>
                      setFormData({ ...formData, uniqueField: e.target.value })
                    }
                    className={formData.uniqueField ? '' : 'border-danger'}
                    disabled={!selectedTable || (fieldsData && fieldsData.length === 0)}
                  >
                    <option value="">-- Select a unique field --</option>
                    {fieldsData && fieldsData.length > 0 ? (
                      fieldsData.map((field) => (
                        <option key={field.fieldName} value={field.fieldName}>
                          {field.fieldName}
                          {field.isPrimaryKey ? ' (Primary Key)' : ''}
                          {field.isIdentity ? ' (Identity)' : ''}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        {selectedTable ? 'Loading fields...' : 'Select a table first'}
                      </option>
                    )}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {fieldsData && fieldsData.length > 0
                      ? `Select the primary key field for this table (${fieldsData.length} fields available)`
                      : selectedTable
                      ? 'Loading fields...'
                      : 'Select a table first to load fields'}
                  </Form.Text>
                  {!formData.uniqueField && selectedTable && fieldsData && fieldsData.length > 0 && (
                    <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>
                      This field is mandatory
                    </div>
                  )}
                </Form.Group>

                <div className="mb-3">
                  <h6>Fields to be registered ({Object.values(selectedFields).filter(Boolean).length} selected)</h6>
                  {fieldsData.length > 0 ? (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <Accordion>
                        <Accordion.Item eventKey="select-all">
                          <Accordion.Header>
                            <input
                              type="checkbox"
                              checked={fieldsData.length > 0 && fieldsData.every((_, idx) => selectedFields[idx])}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (e.target.checked) {
                                  const allSelected = {};
                                  fieldsData.forEach((_, idx) => {
                                    allSelected[idx] = true;
                                  });
                                  setSelectedFields(allSelected);
                                } else {
                                  setSelectedFields({});
                                }
                              }}
                              className="me-2"
                              style={{ cursor: 'pointer' }}
                            />
                            <strong>Select All Fields</strong>
                          </Accordion.Header>
                        </Accordion.Item>
                        {fieldsData.map((field, index) => (
                          <Accordion.Item eventKey={`field-${index}`} key={field.fieldName}>
                            <Accordion.Header>
                              <input
                                type="checkbox"
                                checked={selectedFields[index] || false}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setSelectedFields({
                                    ...selectedFields,
                                    [index]: e.target.checked
                                  });
                                }}
                                className="me-2"
                                style={{ cursor: 'pointer' }}
                              />
                              <span style={{ fontWeight: selectedFields[index] ? 'bold' : 'normal' }}>
                                {field.fieldName}
                                <small className="text-muted ms-2">({field.dataType})</small>
                              </span>
                              <small className="text-muted ms-2">
                                {field.isNullable === 'YES' ? 'Nullable' : 'Required'}
                              </small>
                            </Accordion.Header>
                            <Accordion.Body>
                              <p className="mb-2">
                                <strong>Data Type:</strong> {field.dataType}
                              </p>
                              <p className="mb-0">
                                <strong>Nullable:</strong> {field.isNullable === 'YES' ? 'Yes' : 'No'}
                              </p>
                            </Accordion.Body>
                          </Accordion.Item>
                        ))}
                      </Accordion>
                    </div>
                  ) : (
                    <p className="text-muted">Select a table to see its fields</p>
                  )}
                </div>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddTable}
            disabled={!selectedTable || !formData.uniqueField}
          >
            Register Table
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Table Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Table Metadata</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '85vh', overflowY: 'auto', paddingBottom: '20px' }}>
          <Form>
            {/* Basic Information */}
            <h6 className="mb-3 fw-bold">Basic Information</h6>
            <Form.Group className="mb-3">
              <Form.Label>Table Name (Alternative)</Form.Label>
              <Form.Control
                type="text"
                value={editData.table1}
                onChange={(e) =>
                  setEditData({ ...editData, table1: e.target.value })
                }
                placeholder="Alternative table name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Header Label</Form.Label>
              <Form.Control
                type="text"
                value={editData.headerLabel}
                onChange={(e) =>
                  setEditData({ ...editData, headerLabel: e.target.value })
                }
                placeholder="Display name for the table"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>XML Tag</Form.Label>
              <Form.Control
                type="text"
                value={editData.xmltag}
                onChange={(e) =>
                  setEditData({ ...editData, xmltag: e.target.value })
                }
                placeholder="XML tag name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>View Table (Optional)</Form.Label>
              <Form.Select
                value={editData.viewtable || ''}
                onChange={(e) => {
                  setEditData({ ...editData, viewtable: e.target.value });
                }}
              >
                <option value="">-- No View (Use Table Fields) --</option>
                {(() => {
                  return availableViews.map((view) => (
                    <option key={view.viewName} value={view.viewName}>
                      {view.viewName}
                    </option>
                  ));
                })()}
              </Form.Select>
              <Form.Text className="text-muted">
                Select a view to use its fields instead of the table fields
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Unique Field (Primary Key)</Form.Label>
              <Form.Control
                type="text"
                value={editData.uniqueField || ''}
                onChange={(e) =>
                  setEditData({ ...editData, uniqueField: e.target.value })
                }
                placeholder="Name of unique/primary key field"
              />
            </Form.Group>

            {/* SQL Clauses */}
            <h6 className="mb-3 fw-bold">SQL Configuration</h6>
            <Form.Group className="mb-3">
              <Form.Label>Where Clause (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={editData.where1}
                onChange={(e) =>
                  setEditData({ ...editData, where1: e.target.value })
                }
                placeholder="e.g., status = 1 AND archived = 0"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Order By (Optional)</Form.Label>
              <Form.Control
                type="text"
                value={editData.orderby1}
                onChange={(e) =>
                  setEditData({ ...editData, orderby1: e.target.value })
                }
                placeholder="e.g., OrderDate DESC, Name ASC"
              />
            </Form.Group>

            {/* Display Options */}
            <h6 className="mb-3 fw-bold">Display Options</h6>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Use Distinct Records"
                checked={editData.useDistinct}
                onChange={(e) =>
                  setEditData({ ...editData, useDistinct: e.target.checked })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Searchable"
                checked={editData.searchable}
                onChange={(e) =>
                  setEditData({ ...editData, searchable: e.target.checked })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Show Description"
                checked={editData.showDescription}
                onChange={(e) =>
                  setEditData({ ...editData, showDescription: e.target.checked })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Show Number of Records"
                checked={editData.showNumberofRecords}
                onChange={(e) =>
                  setEditData({ ...editData, showNumberofRecords: e.target.checked })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Alternate Colors"
                checked={editData.alternateColors}
                onChange={(e) =>
                  setEditData({ ...editData, alternateColors: e.target.checked })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Use Alphabetical Header"
                checked={editData.useAlphaHeader}
                onChange={(e) =>
                  setEditData({ ...editData, useAlphaHeader: e.target.checked })
                }
              />
            </Form.Group>

            {/* Link Options */}
            <h6 className="mb-3 fw-bold">Link Options</h6>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Show Long View Link"
                checked={editData.longViewLinkfromTable}
                onChange={(e) =>
                  setEditData({ ...editData, longViewLinkfromTable: e.target.checked })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Show Edit View Link"
                checked={editData.editViewLinkfromTable}
                onChange={(e) =>
                  setEditData({ ...editData, editViewLinkfromTable: e.target.checked })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Show View Child Link"
                checked={editData.viewChildLinkfromTable}
                onChange={(e) =>
                  setEditData({ ...editData, viewChildLinkfromTable: e.target.checked })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Show Add New Link"
                checked={editData.addnewLinkfromTable}
                onChange={(e) =>
                  setEditData({ ...editData, addnewLinkfromTable: e.target.checked })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Show Delete Link"
                checked={editData.deletefromTableLink}
                onChange={(e) =>
                  setEditData({ ...editData, deletefromTableLink: e.target.checked })
                }
              />
            </Form.Group>

            {/* UI Options */}
            <h6 className="mb-3 fw-bold">UI Options</h6>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Allow Radio Button"
                checked={editData.allowRadioButton}
                onChange={(e) =>
                  setEditData({ ...editData, allowRadioButton: e.target.checked })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Allow Checkbox"
                checked={editData.allowCheckbox}
                onChange={(e) =>
                  setEditData({ ...editData, allowCheckbox: e.target.checked })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Use AutoComplete"
                checked={editData.useAC}
                onChange={(e) =>
                  setEditData({ ...editData, useAC: e.target.checked })
                }
              />
            </Form.Group>

            {/* Format Selection */}
            <hr className="my-4" style={{ borderTop: '2px solid #198754', borderWidth: '2px' }} />
            <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', marginBottom: '20px' }}>
              <h6 className="mb-3 fw-bold" style={{ color: '#198754', fontSize: '16px', marginBottom: '10px' }}>
                📋 Format Configuration
              </h6>
              {addFormats.length === 0 && editFormats.length === 0 && searchFormats.length === 0 && searchResultFormats.length === 0 ? (
                <Alert variant="warning" className="mb-3" style={{ fontSize: '12px', marginBottom: '10px' }}>
                  <strong>⚠️ No formats loaded!</strong>
                  <br />
                  <small>
                    Debug: Add={addFormats.length}, Edit={editFormats.length}, Search={searchFormats.length}, SearchResult={searchResultFormats.length}
                    <br />
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={loadFormats}
                      className="mt-2"
                    >
                      🔄 Reload Formats
                    </Button>
                    <br />
                    <small className="text-muted">Check browser console (F12) for errors.</small>
                  </small>
                </Alert>
              ) : (
                <Alert variant="success" className="mb-3" style={{ fontSize: '11px', padding: '8px', marginBottom: '10px' }}>
                  <strong>✓ Formats loaded:</strong> Add ({addFormats.length}), Edit ({editFormats.length}), Search ({searchFormats.length}), SearchResult ({searchResultFormats.length})
                </Alert>
              )}
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Add Record Format</Form.Label>
              <Form.Select
                value={editData.addFormatID || ''}
                onChange={(e) =>
                  setEditData({ ...editData, addFormatID: e.target.value ? parseInt(e.target.value) : null })
                }
              >
                <option value="">-- Use Default Format --</option>
                {addFormats.length === 0 ? (
                  <option disabled>No Add formats available ({addFormats.length})</option>
                ) : (
                  addFormats.map((format) => (
                    <option key={format.FormatID} value={format.FormatID}>
                      {format.FormatName} {format.IsDefault ? '(Default)' : ''}
                    </option>
                  ))
                )}
              </Form.Select>
              <Form.Text className="text-muted">
                Select format template for Add Record form {addFormats.length > 0 && `(${addFormats.length} available)`}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Edit Record Format</Form.Label>
              <Form.Select
                value={editData.editFormatID || ''}
                onChange={(e) =>
                  setEditData({ ...editData, editFormatID: e.target.value ? parseInt(e.target.value) : null })
                }
              >
                <option value="">-- Use Default Format --</option>
                {editFormats.length === 0 ? (
                  <option disabled>No Edit formats available ({editFormats.length})</option>
                ) : (
                  editFormats.map((format) => (
                    <option key={format.FormatID} value={format.FormatID}>
                      {format.FormatName} {format.IsDefault ? '(Default)' : ''}
                    </option>
                  ))
                )}
              </Form.Select>
              <Form.Text className="text-muted">
                Select format template for Edit Record form {editFormats.length > 0 && `(${editFormats.length} available)`}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Search Format</Form.Label>
              <Form.Select
                value={editData.searchFormatID || ''}
                onChange={(e) =>
                  setEditData({ ...editData, searchFormatID: e.target.value ? parseInt(e.target.value) : null })
                }
              >
                <option value="">-- Use Default Format --</option>
                {searchFormats.length === 0 ? (
                  <option disabled>No Search formats available ({searchFormats.length})</option>
                ) : (
                  searchFormats.map((format) => (
                    <option key={format.FormatID} value={format.FormatID}>
                      {format.FormatName} {format.IsDefault ? '(Default)' : ''}
                    </option>
                  ))
                )}
              </Form.Select>
              <Form.Text className="text-muted">
                Select format template for Search form {searchFormats.length > 0 && `(${searchFormats.length} available)`}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Search Result Format</Form.Label>
              <Form.Select
                value={editData.searchResultFormatID || ''}
                onChange={(e) =>
                  setEditData({ ...editData, searchResultFormatID: e.target.value ? parseInt(e.target.value) : null })
                }
              >
                <option value="">-- Use Default Format --</option>
                {searchResultFormats.length === 0 ? (
                  <option disabled>No Search Result formats available ({searchResultFormats.length})</option>
                ) : (
                  searchResultFormats.map((format) => (
                    <option key={format.FormatID} value={format.FormatID}>
                      {format.FormatName} {format.IsDefault ? '(Default)' : ''}
                    </option>
                  ))
                )}
              </Form.Select>
              <Form.Text className="text-muted">
                Select format template for Search Results display {searchResultFormats.length > 0 && `(${searchResultFormats.length} available)`}
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEditTable}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Fields Modal */}
      <Modal show={showFieldsModal} onHide={() => setShowFieldsModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            Edit Fields for {selectedTableForFields?.ResolvedName || selectedTableForFields?.TableName}
          </Modal.Title>
          <Button variant="success" size="sm" onClick={handleAddNewField}>
            <i className="bi bi-plus-circle"></i> Add Field
          </Button>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {fieldsData.length > 0 ? (
            <Accordion>
              {fieldsData.map((field) => {
                const fieldEditData = editingFields[field.FieldsID] || {
                  fieldname: field.fieldname,
                  description: field.description,
                  ordering: field.ordering,
                  addable: field.addable,
                  editable: field.editable,
                  shortViewable: field.shortViewable,
                  searchable: field.searchable,
                  requiredForSearch: field.RequiredForSearch || false,
                  type: field.type || 'text',
                  sqlSource: field.SQLSource || '',
                  sqlDisplay: field.SQLDisplay || '',
                  sqlWhere: field.SQLWhere || '',
                  encrypted: field.Encrypted || false,
                  defaultValue: field.DefaultValue || '',
                  displayLength: field.displayLength || '',
                  maxLength: field.maxLength || '',
                  height: field.height || '',
                  width: field.width || '',
                  datatype: field.datatype || field.dataType || ''
                };

                return (
                  <Accordion.Item eventKey={`field-${field.FieldsID}`} key={`field-${field.FieldsID}`}>
                    <Accordion.Header>
                      <div>
                        <strong>{fieldEditData.fieldname}</strong>
                        {field.isPrimaryKey && (
                          <Badge bg="warning" className="ms-2" text="dark">Primary Key</Badge>
                        )}
                        {field.isIdentity && (
                          <Badge bg="info" className="ms-2">Identity</Badge>
                        )}
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-2">
                            <Form.Label className="fw-bold">Field Name</Form.Label>
                            <Form.Control
                              type="text"
                              value={fieldEditData.fieldname}
                              readOnly={field.FieldsID > 0}
                              onChange={(e) => {
                                if (field.FieldsID < 0) {
                                  setEditingFields({
                                    ...editingFields,
                                    [field.FieldsID]: { ...fieldEditData, fieldname: e.target.value }
                                  });
                                }
                              }}
                            />
                            {field.FieldsID > 0 && (
                              <Form.Text className="text-muted">Field name cannot be edited</Form.Text>
                            )}
                            {field.FieldsID < 0 && (
                              <Form.Text className="text-muted">Set a unique field name</Form.Text>
                            )}
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-2">
                            <Form.Label className="fw-bold">Order</Form.Label>
                            <Form.Control
                              type="number"
                              value={fieldEditData.ordering}
                              onChange={(e) =>
                                setEditingFields({
                                  ...editingFields,
                                  [field.FieldsID]: { ...fieldEditData, ordering: parseInt(e.target.value) }
                                })
                              }
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={fieldEditData.description}
                          onChange={(e) =>
                            setEditingFields({
                              ...editingFields,
                              [field.FieldsID]: { ...fieldEditData, description: e.target.value }
                            })
                          }
                        />
                      </Form.Group>

                      <Row>
                        <Col md={6}>
                          <Form.Check
                            type="checkbox"
                            label="Addable"
                            checked={fieldEditData.addable}
                            onChange={(e) =>
                              setEditingFields({
                                ...editingFields,
                                [field.FieldsID]: { ...fieldEditData, addable: e.target.checked }
                              })
                            }
                            className="mb-2"
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Check
                            type="checkbox"
                            label="Editable"
                            checked={field.isPrimaryKey ? false : fieldEditData.editable}
                            onChange={(e) =>
                              setEditingFields({
                                ...editingFields,
                                [field.FieldsID]: { ...fieldEditData, editable: e.target.checked }
                              })
                            }
                            disabled={field.isPrimaryKey}
                            className="mb-2"
                          />
                          {field.isPrimaryKey && (
                            <Form.Text className="text-warning d-block">Primary keys are not editable</Form.Text>
                          )}
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Check
                            type="checkbox"
                            label="Short Viewable"
                            checked={fieldEditData.shortViewable}
                            onChange={(e) =>
                              setEditingFields({
                                ...editingFields,
                                [field.FieldsID]: { ...fieldEditData, shortViewable: e.target.checked }
                              })
                            }
                            className="mb-2"
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Check
                            type="checkbox"
                            label="Searchable"
                            checked={fieldEditData.searchable}
                            onChange={(e) =>
                              setEditingFields({
                                ...editingFields,
                                [field.FieldsID]: { ...fieldEditData, searchable: e.target.checked, requiredForSearch: e.target.checked ? fieldEditData.requiredForSearch : false }
                              })
                            }
                            className="mb-2"
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Check
                            type="checkbox"
                            label="Required for Search"
                            checked={fieldEditData.requiredForSearch}
                            onChange={(e) =>
                              setEditingFields({
                                ...editingFields,
                                [field.FieldsID]: { ...fieldEditData, requiredForSearch: e.target.checked }
                              })
                            }
                            disabled={!fieldEditData.searchable}
                            className="mb-2"
                          />
                          <Form.Text className="text-muted d-block" style={{ fontSize: '11px' }}>
                            {!fieldEditData.searchable 
                              ? 'Enable "Searchable" first to make this field required for search'
                              : 'This field will be marked as required in the search form'}
                          </Form.Text>
                        </Col>
                      </Row>

                      {/* Field Type Configuration */}
                      <hr className="my-3" />
                      <h6 className="fw-bold mb-3">Field Type Configuration</h6>

                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Field Type</Form.Label>
                        <Form.Select
                          value={fieldEditData.type || 'text'}
                          onChange={(e) =>
                            setEditingFields({
                              ...editingFields,
                              [field.FieldsID]: { ...fieldEditData, type: e.target.value }
                            })
                          }
                        >
                          <option value="">-- Please Select --</option>
                          <option value="text">Text Input</option>
                          <option value="TextArea">TextArea</option>
                          <option value="HTMLEditor">HTMLEditor</option>
                          <option value="Upload">Upload</option>
                          <option value="dropdown">Dropdown</option>
                          <option value="datetime">Date/Time Picker</option>
                          <option value="bit">Checkbox (Boolean)</option>
                          <option value="password">Password</option>
                          <option value="multiselect">Multi-Select Dropdown</option>
                          <option value="radio">Radio Button</option>
                        </Form.Select>
                      </Form.Group>

                      {/* TextArea Controller Options */}
                      {fieldEditData.type === 'TextArea' && (
                        <div style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px', marginBottom: '12px' }}>
                          <h6 className="mb-2">TextArea Options</h6>
                          <Form.Group className="mb-2">
                            <Form.Label size="sm">Rows</Form.Label>
                            <Form.Control
                              type="number"
                              size="sm"
                              min="1"
                              max="20"
                              value={fieldEditData.fieldControllerConfig?.rows || 4}
                              onChange={(e) =>
                                setEditingFields({
                                  ...editingFields,
                                  [field.FieldsID]: { 
                                    ...fieldEditData, 
                                    fieldControllerConfig: {
                                      ...(fieldEditData.fieldControllerConfig || {}),
                                      rows: parseInt(e.target.value)
                                    }
                                  }
                                })
                              }
                            />
                          </Form.Group>
                        </div>
                      )}

                      {/* HTMLEditor Controller Options */}
                      {fieldEditData.type === 'HTMLEditor' && (
                        <div style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px', marginBottom: '12px' }}>
                          <h6 className="mb-2">HTMLEditor Options</h6>
                          <Form.Group className="mb-2">
                            <Form.Label size="sm">Toolbar</Form.Label>
                            <Form.Select
                              size="sm"
                              value={fieldEditData.fieldControllerConfig?.toolbar || 'basic'}
                              onChange={(e) =>
                                setEditingFields({
                                  ...editingFields,
                                  [field.FieldsID]: { 
                                    ...fieldEditData, 
                                    fieldControllerConfig: {
                                      ...(fieldEditData.fieldControllerConfig || {}),
                                      toolbar: e.target.value
                                    }
                                  }
                                })
                              }
                            >
                              <option value="basic">Basic</option>
                              <option value="standard">Standard</option>
                              <option value="full">Full</option>
                            </Form.Select>
                          </Form.Group>
                        </div>
                      )}

                      {/* Upload Controller Options */}
                      {fieldEditData.type === 'Upload' && (
                        <div style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px', marginBottom: '12px' }}>
                          <h6 className="mb-2">Upload Options</h6>
                          <Form.Group className="mb-2">
                            <Form.Label size="sm">Allowed File Types</Form.Label>
                            <Form.Control
                              type="text"
                              size="sm"
                              placeholder="e.g., image/*, .pdf, .docx"
                              value={fieldEditData.fieldControllerConfig?.allowedTypes || 'image/*'}
                              onChange={(e) =>
                                setEditingFields({
                                  ...editingFields,
                                  [field.FieldsID]: { 
                                    ...fieldEditData, 
                                    fieldControllerConfig: {
                                      ...(fieldEditData.fieldControllerConfig || {}),
                                      allowedTypes: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </Form.Group>
                          <Form.Group className="mb-2">
                            <Form.Label size="sm">Max File Size</Form.Label>
                            <Form.Control
                              type="text"
                              size="sm"
                              placeholder="e.g., 5MB, 10MB"
                              value={fieldEditData.fieldControllerConfig?.maxSize || '5MB'}
                              onChange={(e) =>
                                setEditingFields({
                                  ...editingFields,
                                  [field.FieldsID]: { 
                                    ...fieldEditData, 
                                    fieldControllerConfig: {
                                      ...(fieldEditData.fieldControllerConfig || {}),
                                      maxSize: e.target.value
                                    }
                                  }
                                })
                              }
                            />
                          </Form.Group>
                        </div>
                      )}

                      {/* Conditional Fields for Dropdown, Multiselect, Radio */}
                      {['dropdown', 'multiselect', 'radio'].includes(fieldEditData.type) && (
                        <>
                          <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Select Source Table</Form.Label>
                            <Form.Select
                              value={fieldEditData.sqlSource ? fieldEditData.sqlSource.match(/FROM\s+(\w+)/i)?.[1] || '' : ''}
                              onChange={async (e) => {
                                const tableName = e.target.value;
                                if (tableName) {
                                  await fetchDropdownTableFields(tableName);
                                  setEditingFields({
                                    ...editingFields,
                                    [field.FieldsID]: { 
                                      ...fieldEditData, 
                                      sqlSource: `SELECT * FROM ${tableName}`
                                      // Don't reset sqlDisplay - keep it if it was already set
                                    }
                                  });
                                }
                              }}
                            >
                              <option value="">-- Select a table --</option>
                              {dropdownTables.map((table) => (
                                <option key={table.tableName} value={table.tableName}>
                                  {table.tableName}
                                </option>
                              ))}
                            </Form.Select>
                            <Form.Text className="text-muted">Select a table to use as dropdown source</Form.Text>
                          </Form.Group>

                          {/* Display Field Selector */}
                          {fieldEditData.sqlSource && (
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-bold">Display Field</Form.Label>
                              <Form.Select
                                value={fieldEditData.sqlDisplay || ''}
                                onChange={(e) =>
                                  setEditingFields({
                                    ...editingFields,
                                    [field.FieldsID]: { ...fieldEditData, sqlDisplay: e.target.value }
                                  })
                                }
                              >
                                <option value="">-- Select a field --</option>
                                {(() => {
                                  const tableName = fieldEditData.sqlSource.match(/FROM\s+(\w+)/i)?.[1];
                                  const tableFields = tableName ? dropdownTableFields[tableName] || [] : [];
                                  return tableFields
                                    .filter(f => !f.isPrimaryKey) // Exclude primary key from display
                                    .map((field) => (
                                      <option key={field.fieldName} value={field.fieldName}>
                                        {field.fieldName}
                                      </option>
                                    ));
                                })()}
                              </Form.Select>
                              <Form.Text className="text-muted">Field to display in dropdown (excluding primary key)</Form.Text>
                            </Form.Group>
                          )}

                          <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">WHERE Clause (Optional)</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={fieldEditData.sqlWhere}
                              onChange={(e) =>
                                setEditingFields({
                                  ...editingFields,
                                  [field.FieldsID]: { ...fieldEditData, sqlWhere: e.target.value }
                                })
                              }
                              placeholder="e.g., Active = 1"
                            />
                            <Form.Text className="text-muted">Filter criteria for source table</Form.Text>
                          </Form.Group>

                          {/* Sub Record ID Field for Cross-Table Configuration */}
                          {fieldEditData.type === 'multiselect' && (
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-bold">Cross-Table Configuration (Sub Record ID)</Form.Label>
                              <Form.Select
                                value={fieldEditData.subRecordID !== null && fieldEditData.subRecordID !== undefined ? String(fieldEditData.subRecordID) : ''}
                                onChange={(e) => {
                                  const newValue = e.target.value ? parseInt(e.target.value) : null;
                                  setEditingFields({
                                    ...editingFields,
                                    [field.FieldsID]: { ...fieldEditData, subRecordID: newValue }
                                  });
                                }}
                              >
                                <option value="">-- None (regular field) --</option>
                                {allXbyXConfigs && allXbyXConfigs.map(config => (
                                  <option key={config.MGB_XbyXID} value={String(config.MGB_XbyXID)}>
                                    {config.LeftTable} ↔ {config.RightTable}
                                  </option>
                                ))}
                              </Form.Select>
                              <Form.Text className="text-muted">Select a cross-table configuration for multi-select fields</Form.Text>
                            </Form.Group>
                          )}
                        </>
                      )}

                      {/* Conditional Field for Password */}
                      {fieldEditData.type === 'password' && (
                        <>
                          <Form.Check
                            type="checkbox"
                            label="Encrypt Password"
                            checked={fieldEditData.encrypted}
                            onChange={(e) =>
                              setEditingFields({
                                ...editingFields,
                                [field.FieldsID]: { ...fieldEditData, encrypted: e.target.checked }
                              })
                            }
                            className="mb-2"
                          />
                        </>
                      )}

                      {/* Field Dimensions and Data Type */}
                      <hr className="my-3" />
                      <h6 className="fw-bold mb-3">Field Dimensions & Data Type</h6>
                      <Row>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Display Length</Form.Label>
                            <Form.Control
                              type="text"
                              value={fieldEditData.displayLength || ''}
                              onChange={(e) => {
                                setEditingFields({
                                  ...editingFields,
                                  [field.FieldsID]: { ...fieldEditData, displayLength: e.target.value }
                                });
                              }}
                              placeholder="e.g., 50"
                            />
                            <Form.Text className="text-muted">Display width in characters</Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Max Length</Form.Label>
                            <Form.Control
                              type="text"
                              value={fieldEditData.maxLength || ''}
                              onChange={(e) => {
                                setEditingFields({
                                  ...editingFields,
                                  [field.FieldsID]: { ...fieldEditData, maxLength: e.target.value }
                                });
                              }}
                              placeholder="e.g., 255"
                            />
                            <Form.Text className="text-muted">Maximum input length</Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Height (px)</Form.Label>
                            <Form.Control
                              type="text"
                              value={fieldEditData.height || ''}
                              onChange={(e) =>
                                setEditingFields({
                                  ...editingFields,
                                  [field.FieldsID]: { ...fieldEditData, height: e.target.value }
                                })
                              }
                              placeholder="e.g., 100"
                            />
                            <Form.Text className="text-muted">Field height in pixels</Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Width (px)</Form.Label>
                            <Form.Control
                              type="text"
                              value={fieldEditData.width || ''}
                              onChange={(e) =>
                                setEditingFields({
                                  ...editingFields,
                                  [field.FieldsID]: { ...fieldEditData, width: e.target.value }
                                })
                              }
                              placeholder="e.g., 300"
                            />
                            <Form.Text className="text-muted">Field width in pixels</Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>

                      {/* Default Value for all types */}
                      <hr className="my-3" />
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Default Value (Optional)</Form.Label>
                        <Form.Control
                          type="text"
                          value={fieldEditData.defaultValue}
                          onChange={(e) =>
                            setEditingFields({
                              ...editingFields,
                              [field.FieldsID]: { ...fieldEditData, defaultValue: e.target.value }
                            })
                          }
                          placeholder="Default value for this field (use 'getdate' for datetime fields)"
                        />
                      </Form.Group>
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          ) : (
            <p className="text-muted">No fields found for this table</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFieldsModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSaveFieldChanges}>
            Save Field Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default MGBSetup;
